/* eslint-disable @typescript-eslint/no-use-before-define */
import React from 'react';
import { takeLatest, call, all, select, put } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Components
import { ModalBulkErrors } from 'components/ModalBulkErrors';

// Services
import { segmentationsApi } from 'services/apiDigitalTicket/segmentations';
import { auth } from 'services/firebase';
import { sendPasswordResetEmail } from 'firebase/auth';

// Constants
import ENVS from 'constants/enviroments';
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

// Store
import store from 'store';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as SegmentationsActions from './actions';

// Types
import { SegmentationsTypes } from './types';

function* listSegmentations({
  payload,
}: ReturnType<typeof SegmentationsActions.listSegmentationsRequest>): any {
  try {
    const { body, filtering } = payload;
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando segmentações' }),
    );

    const transportOperator = yield select(
      (state: any) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(segmentationsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        SegmentationsActions.listSegmentationsSuccess({
          segmentations: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - listSegmentations - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.listSegmentationsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listSegmentationValues({
  payload,
}: ReturnType<typeof SegmentationsActions.listSegmentationValuesRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando segmentações' }),
    );

    const transportOperator = yield select(
      (state: any) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(segmentationsApi.listValues, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SegmentationsActions.listSegmentationValuesSuccess({
          segmentationValues: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - listSegmentationsValues - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.listSegmentationValuesFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({
  payload,
}: ReturnType<typeof SegmentationsActions.getSegmentationRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state: any) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(segmentationsApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SegmentationsActions.getSegmentationSuccess({
          segmentationData: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - getSegmentation - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.getSegmentationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* create({
  payload,
}: ReturnType<typeof SegmentationsActions.createSegmentationRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando segmentação' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(segmentationsApi.create, {
      data: { ...payload, transportOperatorId: transportOperator.id },
    });

    if (response.status === 200) {
      yield put(SegmentationsActions.createSegmentationSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Segmentação criada com sucesso',
        }),
      );
      yield put(push(PATH.SEGMENTATIONS.BASE_URL));
      yield put(SegmentationsActions.listSegmentationsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - createSegmentation - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.createSegmentationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof SegmentationsActions.updateSegmentationRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando segmentação' }));
    const response = yield call(segmentationsApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(SegmentationsActions.updateSegmentationSuccess());
      yield put(DialogsActions.setClose());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Segmentação editada com sucesso',
        }),
      );
      yield put(push(PATH.SEGMENTATIONS.BASE_URL));

      yield put(SegmentationsActions.listSegmentationsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - updateSegmentation - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.updateSegmentationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof SegmentationsActions.deleteSegmentationRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo segmentação' }),
    );
    const response = yield call(segmentationsApi.delete, {
      params: { ...payload },
    });

    if (response.status === 200) {
      yield put(SegmentationsActions.deleteSegmentationSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Segmentação excluída com sucesso',
        }),
      );
      yield put(SegmentationsActions.listSegmentationsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - deleteSegmentation - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.deleteSegmentationFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createList({
  payload,
}: ReturnType<typeof SegmentationsActions.createSegmentationsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando segmentações' }),
    );
    const response = yield call(segmentationsApi.create, {
      data: [...payload],
    });

    if (response.status === 200) {
      yield put(SegmentationsActions.createSegmentationsSuccess());

      yield all(
        response.data.success.map(({ email }: any) =>
          call(() =>
            sendPasswordResetEmail(auth, email, {
              url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
            }),
          ),
        ),
      );

      if (response.data.success.length > 0) {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: `${response.data.success.length} Segmentações cadastradas com sucesso.`,
          }),
        );
      }

      if (response.data.error.length > 0) {
        yield put(DialogsActions.setClose());

        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.map((element: any) => {
            const message = errorMessagersList(element.error);
            return {
              primaryText: element.name,
              secondaryText: message,
            };
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(SegmentationsActions.listSegmentationsRequest({}));
            },
          }),
        );
      } else {
        yield put(DialogsActions.setClose());

        yield put(SegmentationsActions.listSegmentationsRequest({}));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - createSegmentation - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.createSegmentationsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateList({
  payload,
}: ReturnType<typeof SegmentationsActions.updateSegmentationsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Editando segmentações' }),
    );
    const response = yield call(segmentationsApi.update, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(SegmentationsActions.updateSegmentationsSuccess());
      yield put(DialogsActions.setClose());

      if (response.data.error.length > 0) {
        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.map((element: any) => {
            const message = errorMessagersList(element.error);
            return {
              primaryText: element.name,
              secondaryText: message,
            };
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(SegmentationsActions.listSegmentationsRequest({}));
            },
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Segmentações editadas com sucesso',
          }),
        );
        yield put(SegmentationsActions.listSegmentationsRequest({}));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - updateSegmentations - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.updateSegmentationsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeList({
  payload,
}: ReturnType<typeof SegmentationsActions.deleteSegmentationsRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Excluindo segmentações' }),
    );
    const { ids } = payload as { ids: string[] };

    const responses = yield all(
      ids.map((id) =>
        call(segmentationsApi.delete, { params: { id } }),
      ),
    );

    const allSucceeded = responses.every((r: { status: number }) => r.status === 200);

    if (allSucceeded) {
      yield put(SegmentationsActions.deleteSegmentationsSuccess());
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Segmentações excluídas com sucesso',
        }),
      );
      yield put(SegmentationsActions.listSegmentationsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('segmentations - deleteSegmentations - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(SegmentationsActions.deleteSegmentationsFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-profile import/prefer-default-export
export const segmentationsSagas = [
  takeLatest(SegmentationsTypes.LIST_SEGMENTATIONS_REQUEST, listSegmentations),
  takeLatest(
    SegmentationsTypes.LIST_SEGMENTATION_VALUES_REQUEST,
    listSegmentationValues,
  ),
  takeLatest(SegmentationsTypes.GET_SEGMENTATION_REQUEST, get),
  takeLatest(SegmentationsTypes.CREATE_SEGMENTATION_REQUEST, create),
  takeLatest(SegmentationsTypes.UPDATE_SEGMENTATION_REQUEST, update),
  takeLatest(SegmentationsTypes.DELETE_SEGMENTATION_REQUEST, remove),
  takeLatest(SegmentationsTypes.CREATE_SEGMENTATIONS_REQUEST, createList),
  takeLatest(SegmentationsTypes.UPDATE_SEGMENTATIONS_REQUEST, updateList),
  takeLatest(SegmentationsTypes.DELETE_SEGMENTATIONS_REQUEST, removeList),
];
